/**
 * Хранилище «ключ — значение» — единая точка, через которую идут все
 * обращения к постоянной памяти устройства (docs/MOBILE.md, шаг 1).
 *
 * Зачем фасад: в браузере хранилище синхронное (localStorage), в
 * React Native — асинхронное (AsyncStorage). Развести async/await по всем
 * местам, которые сегодня читают секрет партии или настройку, — значит
 * переписать их все и разнести платформу по прикладному коду. Вместо этого
 * интерфейс синхронный, а разницу платформ гасит реализация:
 *
 * - веб читает localStorage напрямую — он и есть синхронный
 *   (`browserKv` ниже). Поведение не меняется вообще, включая видимость
 *   записей, сделанных снаружи (этим пользуются тесты);
 * - приложение собирает `memoryKv` поверх асинхронного драйвера: одна
 *   гидратация в память при старте, синхронные геттеры поверх памяти,
 *   запись отложенная.
 */

/** Синхронный фасад хранилища. Ни один метод не бросает — см. browserKv. */
export interface KvStore {
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
  keys(): string[]
}

/**
 * Асинхронный драйвер платформы — то, что умеет AsyncStorage в RN.
 * Вебу он не нужен: localStorage синхронный и обходится без прослойки.
 */
export interface AsyncKvDriver {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
  keys(): Promise<string[]>
}

/**
 * Синхронный фасад поверх асинхронного драйвера.
 *
 * Снимок памяти приезжает параметром: гидратация (`keys()` + чтение всех
 * значений) — единственная асинхронная часть, и происходит она до старта
 * приложения, а не на ходу. Дальше чтение идёт из памяти, а запись
 * откладывается в драйвер цепочкой — порядок записей одного ключа
 * сохраняется, и упавшая запись не роняет следующие.
 *
 * Ошибки драйвера глотаются тем же рассуждением, что и приватный режим в
 * браузере: из-за хранилища не падает ничего, худшее последствие — правка
 * не переживёт перезапуск.
 */
export function memoryKv(driver: AsyncKvDriver, snapshot: Record<string, string>): KvStore {
  const memory = new Map(Object.entries(snapshot))
  let queue: Promise<unknown> = Promise.resolve()

  const defer = (op: () => Promise<void>): void => {
    queue = queue.then(op).catch(() => {})
  }

  return {
    get: (key) => memory.get(key) ?? null,
    set: (key, value) => {
      memory.set(key, value)
      defer(() => driver.set(key, value))
    },
    remove: (key) => {
      memory.delete(key)
      defer(() => driver.remove(key))
    },
    keys: () => [...memory.keys()],
  }
}

/**
 * Фасад поверх localStorage — прямой, без памяти посередине.
 *
 * Каждый вызов в try/catch: в приватном режиме браузер бросает на запись
 * (а кое-где и на чтение), и из-за этого не должно падать ни приложение,
 * ни партия — без секрета игрок просто окажется зрителем и войдёт заново.
 */
export function browserKv(storage: Storage): KvStore {
  return {
    get(key) {
      try {
        return storage.getItem(key)
      } catch {
        return null
      }
    },
    set(key, value) {
      try {
        storage.setItem(key, value)
      } catch {
        // Приватный режим: играть можно, перезагрузку страницы правка
        // не переживёт.
      }
    },
    remove(key) {
      try {
        storage.removeItem(key)
      } catch {
        // см. set()
      }
    },
    keys() {
      try {
        const names: string[] = []

        for (let at = 0; at < storage.length; at++) {
          const name = storage.key(at)
          if (name !== null) names.push(name)
        }

        return names
      } catch {
        return []
      }
    },
  }
}

/** Драйвер-пустышка: всё забывает. Запасной выход, когда хранилища нет вовсе. */
const nowhereDriver: AsyncKvDriver = {
  get: () => Promise.resolve(null),
  set: () => Promise.resolve(),
  remove: () => Promise.resolve(),
  keys: () => Promise.resolve([]),
}

/**
 * Точка внедрения платформы.
 *
 * Единственное место, которое знает, что под нами браузер: прикладной код
 * импортирует `kv`, и его не волнует, что за ним стоит. В React Native эта
 * константа собирается иначе — `memoryKv` поверх AsyncStorage, снимок
 * гидрируется до первого рендера, — а импорты остаются теми же.
 *
 * Само обращение `window.localStorage` тоже бывает броском (отключённые
 * куки), поэтому и оно под охранником: тогда работаем на памяти до конца
 * сессии.
 */
export const kv: KvStore = (() => {
  try {
    return browserKv(window.localStorage)
  } catch {
    return memoryKv(nowhereDriver, {})
  }
})()
